/**
 * Miscellaneous Module
 *
 * Handles various small utilities including:
 * - Import file display
 * - Page slug auto-generation
 * - Timeslot day checkboxes
 * - Timeclock bulk actions
 * - Theme switching
 */

import { $, $$, ready } from '../utils/dom.js';

// =============================================================================
// Import File Upload
// =============================================================================

/**
 * Display selected file name for import
 * @param {HTMLInputElement} input - File input element
 */
export function displayFileName(input) {
    const fileName = input.files[0]?.name;
    const fileNameElement = $('#file-name');

    if (fileNameElement) {
        if (fileName) {
            fileNameElement.textContent = fileName;
        } else {
            fileNameElement.textContent = '';
        }
    }
}

// =============================================================================
// Page Slug Generation
// =============================================================================

/**
 * Initialize page slug auto-generation
 */
function initPageSlugGeneration() {
    const titleInput = $('#title');
    const slugInput = $('#slug');

    if (!titleInput || !slugInput) return;

    titleInput.addEventListener('input', function() {
        // Only auto-generate if slug is empty
        if (!slugInput.value) {
            let slug = titleInput.value
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
            slugInput.placeholder = slug || 'page-slug';
        }
    });
}

/**
 * Generate slug from text
 * @param {string} text
 * @returns {string}
 */
export function generateSlug(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

// =============================================================================
// Timeslot Day Checkboxes
// =============================================================================

/**
 * Initialize timeslot day checkbox visual feedback
 */
function initTimeslotDayCheckboxes() {
    const checkboxes = $$('input[name="days[]"]');

    checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', function() {
            const label = this.closest('label');
            if (!label) return;

            if (this.checked) {
                label.classList.add('bg-emerald-50', 'border-emerald-500');
            } else {
                label.classList.remove('bg-emerald-50', 'border-emerald-500');
            }
        });

        // Set initial state
        if (checkbox.checked) {
            const label = checkbox.closest('label');
            if (label) {
                label.classList.add('bg-emerald-50', 'border-emerald-500');
            }
        }
    });
}

// =============================================================================
// Timeclock Bulk Actions
// =============================================================================

/**
 * Approve selected time entries
 */
export function approveSelected() {
    const checkboxes = $$('input[type="checkbox"]:checked');
    const ids = Array.from(checkboxes).map(cb => cb.value).filter(v => v);

    if (ids.length === 0) {
        alert('Please select at least one entry to approve.');
        return;
    }

    if (confirm(`Approve ${ids.length} time entries?`)) {
        // Submit form with selected IDs
        const form = $('#bulk-approve-form');
        if (form) {
            const idsInput = form.querySelector('input[name="ids"]');
            if (idsInput) idsInput.value = ids.join(',');
            form.submit();
        }
    }
}

/**
 * Export selected time entries
 */
export function exportSelected() {
    const checkboxes = $$('input[type="checkbox"]:checked');
    const ids = Array.from(checkboxes).map(cb => cb.value).filter(v => v);

    if (ids.length === 0) {
        alert('Please select at least one entry to export.');
        return;
    }

    // Redirect to export URL with selected IDs
    window.location.href = '/timeclock/export?ids=' + ids.join(',');
}

// =============================================================================
// Theme Switching
// =============================================================================

/**
 * Preview a theme
 * @param {string} themeId
 */
export function previewTheme(themeId) {
    window.location.href = `/themes/preview?theme=${encodeURIComponent(themeId)}`;
}

/**
 * Activate a theme
 * @param {string} themeId
 */
export function activateTheme(themeId) {
    if (confirm('Activate this theme? Your current theme will be replaced.')) {
        window.location.href = `/themes/${themeId}/activate`;
    }
}

// =============================================================================
// Waitlist
// =============================================================================

/**
 * Initialize waitlist form
 */
function initWaitlistForm() {
    const form = $('#waitlist-form');
    if (!form) return;

    const phoneInput = $('#phone');
    if (phoneInput) {
        // Format phone number as user types
        phoneInput.addEventListener('input', function() {
            let value = this.value.replace(/\D/g, '');
            if (value.length > 10) value = value.slice(0, 10);

            if (value.length >= 6) {
                this.value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
            } else if (value.length >= 3) {
                this.value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
            } else if (value.length > 0) {
                this.value = `(${value}`;
            }
        });
    }
}

// =============================================================================
// Cart Quantity Updates
// =============================================================================

/**
 * Update cart item quantity
 * @param {string} itemId
 * @param {number} delta - Change amount (+1 or -1)
 */
export function updateCartQuantity(itemId, delta) {
    const quantityEl = $(`#quantity-${itemId}`);
    if (!quantityEl) return;

    let quantity = parseInt(quantityEl.textContent) || 1;
    quantity = Math.max(1, quantity + delta);
    quantityEl.textContent = quantity;

    // Trigger cart update
    const event = new CustomEvent('cart:update', {
        detail: { itemId, quantity }
    });
    document.dispatchEvent(event);
}

/**
 * Remove item from cart
 * @param {string} itemId
 */
export function removeFromCart(itemId) {
    if (confirm('Remove this item from cart?')) {
        const event = new CustomEvent('cart:remove', {
            detail: { itemId }
        });
        document.dispatchEvent(event);
    }
}

// =============================================================================
// Auto-Initialization
// =============================================================================

function initMisc() {
    initPageSlugGeneration();
    initTimeslotDayCheckboxes();
    initWaitlistForm();
}

ready(initMisc);

// Expose globally
window.displayFileName = displayFileName;
window.generateSlug = generateSlug;
window.approveSelected = approveSelected;
window.exportSelected = exportSelected;
window.previewTheme = previewTheme;
window.activateTheme = activateTheme;
window.updateCartQuantity = updateCartQuantity;
window.removeFromCart = removeFromCart;

export default {
    displayFileName,
    generateSlug,
    approveSelected,
    exportSelected,
    previewTheme,
    activateTheme,
    updateCartQuantity,
    removeFromCart
};
